import { useState, useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'

/**
 * Tracks iOS keyboard height using Capacitor's Keyboard plugin events.
 *
 * Capacitor is configured with Keyboard.resize: 'none', so the WebView
 * does NOT resize when the keyboard appears — `position: fixed; bottom: 0`
 * sits behind the keyboard. Consumers should offset `bottom` by
 * `keyboardHeight` to keep elements visible above it.
 *
 * Returns:
 *  - keyboardHeight: keyboard height in CSS px (0 when closed)
 *  - viewportHeight: visible height above the keyboard (px)
 *  - keyboardOpen: true when the keyboard is visible
 */
export function useKeyboardHeight() {
  const [keyboardHeight, setKeyboardHeight] = useState(0)

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const showListener = Keyboard.addListener('keyboardWillShow', (info) => {
      setKeyboardHeight(info.keyboardHeight)
    })

    const hideListener = Keyboard.addListener('keyboardWillHide', () => {
      setKeyboardHeight(0)
    })

    return () => {
      showListener.then(h => h.remove())
      hideListener.then(h => h.remove())
    }
  }, [])

  const fullHeight = window.innerHeight
  const viewportHeight = keyboardHeight > 0 ? fullHeight - keyboardHeight : fullHeight

  return { keyboardHeight, viewportHeight, keyboardOpen: keyboardHeight > 0 }
}
